import React from "react";

import { authorizedFetch } from "./boot.js";
import { Card } from "./ui.jsx";
import { visiblePoll } from "./stats_shared.js";
import { AdminSaleHistory, useHistorySales } from "./admin_sale_history.jsx";
import {
  BANDS, BandLegend, ByBand, StandingCell, StandingNotes, StandingPanel, standingHeadline,
} from "./stats_standing.jsx";

// The Inglis admin console's Repository stats (DECISIONS.md, 26 Sep 2026): each
// horse placed against the whole sale, for every lot or one vendor's. The
// standing was built for vendors on 25 September and moved here the next day;
// vendors are sent none. Everything shown comes from admin.stats.standing@1,
// whose policy admits Inglis administrators holding that exact read, and the
// vendor list from admin.vendors.list@1.

const { useEffect, useMemo, useState } = React;

// Our copy of the views syncs once a minute; reading faster only repeats it.
const REFRESH_MS = 60_000;

async function read(path) {
  const response = await authorizedFetch(`/collections/${path}`);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(`request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return body;
}

const breeding = (row) => [row?.sire, row?.dam].filter(Boolean).join(" × ");
const lotOrder = (a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true });
const count = (value) => (typeof value === "number" ? value.toLocaleString() : "—");

function failure(error) {
  if (!error) return "";
  // Before the Inglis admin grant carries the read, the read is undiscoverable.
  if (error.status === 404) return "Repository stats are not available to this administrator yet.";
  if (error.status === 401) return "Sign in again to read Repository stats.";
  return "Repository stats could not be loaded.";
}

// The tab: the live sale, or a past sale replayed from packaged history.
export function AdminRepositoryStats({ sale = "26R2R" }) {
  const history = useHistorySales();
  const [chosen, setChosen] = useState("live");
  return (
    <div className="admin-repository-stats">
      <label className="admin-standing-pick admin-sale-pick">
        <span className="qstat-label">Sale</span>
        <select value={chosen} onChange={(event) => setChosen(event.target.value)} aria-label="Sale">
          <option value="live">{sale} — live</option>
          {history.length > 0 && (
            <optgroup label="Past sales (replayed)">
              {history.map((row) => <option key={row.key} value={row.key}>{row.name}</option>)}
            </optgroup>
          )}
        </select>
      </label>
      {chosen === "live" ? <LiveStanding sale={sale}/> : <AdminSaleHistory key={chosen} sale={chosen}/>}
    </div>
  );
}

function LiveStanding({ sale }) {
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [layout, setLayout] = useState("list");
  const [order, setOrder] = useState("band");
  const [open, setOpen] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let live = true;
    read(`admin.vendors.list@1/?${new URLSearchParams({ sale, limit: "100" })}`)
      .then((body) => { if (live) setVendors(Array.isArray(body.items) ? body.items : []); })
      .catch(() => { if (live) setVendors([]); });
    return () => { live = false; };
  }, [sale]);

  useEffect(() => {
    let live = true;
    setOpen(null);
    const params = new URLSearchParams({ sale });
    if (vendor) params.set("vendor", vendor);
    const load = () => read(`admin.stats.standing@1/?${params}`)
      .then((body) => {
        if (!live) return;
        // A response for a different selection is never shown as this one.
        if (body.sale !== sale || (body.vendor || "") !== vendor || !Array.isArray(body.lots)) {
          setError({ status: 0 });
          return;
        }
        setError(null);
        setResult(body);
      })
      .catch((caught) => { if (live) setError(caught); });
    setResult(null);
    load();
    const stop = visiblePoll(load, REFRESH_MS);
    return () => { live = false; stop(); };
  }, [sale, vendor, retry]);

  const standing = useMemo(() => result && {
    status: result.status,
    stale: result.stale,
    asOf: result.asOf,
    earlyDays: result.earlyDays,
    lodgements: result.lodgements,
    lots: Object.fromEntries(result.lots.map((row) => [row.lotId, row])),
  }, [result]);
  const numbers = useMemo(() => Object.fromEntries((result?.lots || []).map((row) => [row.lotId, row.number])), [result]);
  const horses = useMemo(() => Object.fromEntries((result?.lots || []).map((row) => [row.lotId, breeding(row)])), [result]);
  const rows = useMemo(() => {
    const place = (row) => BANDS.indexOf(row.band);
    return (result?.lots || []).slice().sort((a, b) =>
      (order === "band" ? place(a) - place(b) || (b.buyersVets || 0) - (a.buyersVets || 0) : 0) || lotOrder(a, b));
  }, [result, order]);

  const all = !vendor;
  const ranked = Boolean(standing && standing.status !== "unavailable");
  const seg = (current, pick, options, label) => (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([id, text]) => (
        <button key={id} type="button" className={current === id ? "on" : ""} aria-pressed={current === id}
                onClick={() => pick(id)}>{text}</button>
      ))}
    </div>
  );
  const columns = all ? 7 : 6;

  return (
    <Card title="Repository stats" subtitle={`Each horse against the whole of ${sale} · Inglis only`}
          className="flush admin-standing">
      <div className="standing-head">
        <div className="standing-controls">
          <label className="admin-standing-pick">
            <span className="qstat-label">Show</span>
            <select value={vendor} onChange={(event) => setVendor(event.target.value)} aria-label="Lots to show">
              <option value="">All lots</option>
              {vendors.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          {ranked && seg(layout, setLayout, [["list", "List"], ["bands", "By band"]], "Layout")}
          {ranked && layout === "list" && seg(order, setOrder, [["band", "Band first"], ["lot", "Lot order"]], "Order")}
          <button type="button" className="btn" onClick={() => setRetry((value) => value + 1)}>Refresh</button>
        </div>
        {ranked && <span className="caption">{standingHeadline(standing)}</span>}
        {standing && <StandingNotes standing={standing} numbers={numbers}/>}
        {error && <p role="alert" className="standing-note action">{failure(error)}</p>}
        {!result && !error && <p role="status" className="caption">Loading Repository stats…</p>}
      </div>

      {ranked && layout === "bands" && (
        <ByBand standing={standing} lots={rows} numbers={numbers} horses={horses}
                onOpen={(lotId) => { setLayout("list"); setOpen(lotId); }}/>
      )}

      {ranked && layout === "list" && (
        <table className="table stack-table">
          <thead><tr>
            <th>Lot</th>
            <th>Horse</th>
            {all && <th>Vendor</th>}
            <th className="standing-col">In the sale</th>
            <th style={{ textAlign: "right" }}>X-ray views</th>
            <th style={{ textAlign: "right" }}>Vets</th>
            <th style={{ textAlign: "right" }}>Not counted</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns} className="stack-full" style={{ textAlign: "center", padding: 36, color: "var(--fg-muted)" }}>
                {all ? "No reviewed, live lots in this sale." : "This vendor has no reviewed, live lots in this sale."}
              </td></tr>
            )}
            {rows.flatMap((row) => {
              const expanded = open === row.lotId;
              const skipped = (row.uncounted?.noBuyer || 0) + (row.uncounted?.lodgingVet || 0);
              const out = [
                <tr key={row.lotId}>
                  <td data-label="Lot">
                    <button type="button" className="lot-detail-link" aria-expanded={expanded ? "true" : "false"}
                            aria-label={`${expanded ? "Close" : "Open"} standing for lot ${row.number}`}
                            onClick={() => setOpen(expanded ? null : row.lotId)}>{row.number}</button>
                  </td>
                  <td data-label="Horse">{breeding(row) || "—"}</td>
                  {all && <td data-label="Vendor">{row.vendorName}</td>}
                  <td data-label="In the sale" className="standing-col">
                    <StandingCell entry={row} stale={standing.stale} viewed={row.xrayViews > 0}/>
                  </td>
                  <td data-label="X-ray views" style={{ textAlign: "right" }}>{count(row.xrayViews)}</td>
                  <td data-label="Vets" style={{ textAlign: "right" }}>{count(row.vets)}</td>
                  <td data-label="Not counted" style={{ textAlign: "right" }}>{skipped ? skipped.toLocaleString() : "—"}</td>
                </tr>,
              ];
              if (expanded) {
                out.push(
                  <tr key={`${row.lotId}:panel`} className="admin-standing-panel">
                    <td colSpan={columns} className="stack-full"><StandingPanel standing={standing} lotId={row.lotId}/></td>
                  </tr>,
                );
              }
              return out;
            })}
          </tbody>
        </table>
      )}

      {ranked && <BandLegend standing={standing} collapsible defaultOpen={false}/>}
    </Card>
  );
}
